import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;
    const signal = this.#abortController.signal;

    videoElement.muted = true;
    videoElement.defaultMuted = true;
    videoElement.autoplay = true;
    videoElement.playsInline = true;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.addEventListener('loadeddata', this.#ensurePlayback, { signal });
    videoElement.addEventListener('canplay', this.#ensurePlayback, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });

    videoElement.load();
    this.#ensurePlayback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #handleVisibilityChange = () => {
    if (!document.hidden) this.#ensurePlayback();
  };

  #ensurePlayback = () => {
    const { videoElement } = this.refs;

    if (!(videoElement instanceof HTMLVideoElement) || document.hidden) return;

    const playPromise = videoElement.play();

    if (playPromise instanceof Promise) {
      playPromise.catch(() => {
        // Some browsers reject the first autoplay attempt until media is ready.
      });
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
